import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Heart, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import UserAvatar from '../components/UserAvatar';
import { useAuth } from '../contexts/AuthContext';
import { PLANBASE_CONFIG } from '../config';
import { useProjectInteractions } from '../hooks/useProjectInteractions';
import { useProjectViews } from '../hooks/useProjectViews';
import { useHideBottomNav } from '../hooks/useHideBottomNav';

// Lightbox Component
function Lightbox({ 
  images, 
  currentIndex, 
  onClose, 
  onPrev, 
  onNext,
  tags 
}: { 
  images: any[]; 
  currentIndex: number; 
  onClose: () => void; 
  onPrev: () => void; 
  onNext: () => void;
  tags?: string[];
}) {
  const currentImage = images[currentIndex];
  const imageUrl = typeof currentImage === 'string' ? currentImage : currentImage?.url;
  const imageTitle = typeof currentImage === 'object' ? currentImage?.title : null;
  const imageDescription = typeof currentImage === 'object' ? currentImage?.description : null;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 md:p-4 text-white shrink-0">
        <span className="text-sm">{currentIndex + 1} / {images.length}</span>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <X className="h-5 w-5 md:h-6 md:w-6" />
        </button>
      </div>

      {/* Main Image Area */}
      <div className="flex-1 flex items-center justify-center relative px-2 md:px-4 min-h-0" onClick={(e) => e.stopPropagation()}>
        {/* Prev Button - Hidden on mobile, use swipe instead */}
        {images.length > 1 && (
          <button 
            onClick={onPrev}
            className="hidden md:flex absolute left-2 md:left-4 p-2 md:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <Icon icon="hugeicons:arrow-left-02" className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </button>
        )}

        {/* Image */}
        <img
          src={imageUrl}
          alt={imageTitle || `Image ${currentIndex + 1}`}
          className="max-h-[60vh] md:max-h-[70vh] max-w-full object-contain"
          onError={(e) => {
            e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
          }}
        />

        {/* Next Button - Hidden on mobile */}
        {images.length > 1 && (
          <button 
            onClick={onNext}
            className="hidden md:flex absolute right-2 md:right-4 p-2 md:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <Icon icon="hugeicons:arrow-right-02" className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </button>
        )}
      </div>

      {/* Mobile Navigation Buttons */}
      {images.length > 1 && (
        <div className="flex md:hidden justify-center gap-4 py-2">
          <button 
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <Icon icon="hugeicons:arrow-left-02" className="h-5 w-5 text-white" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <Icon icon="hugeicons:arrow-right-02" className="h-5 w-5 text-white" />
          </button>
        </div>
      )}

      {/* Footer with Title, Description, and Tags */}
      <div className="p-3 md:p-4 text-white shrink-0 max-h-[30vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-3xl mx-auto space-y-2 md:space-y-3">
          {imageTitle && (
            <h3 className="text-base md:text-lg font-semibold text-center">{imageTitle}</h3>
          )}
          {imageDescription && (
            <p className="text-xs md:text-sm text-white/80 text-center line-clamp-3 md:line-clamp-none">{imageDescription}</p>
          )}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 pt-1 md:pt-2">
              {tags.map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 text-xs bg-white/10 text-white/90 rounded-full"
                >
                  <Icon icon="hugeicons:tag-01" className="h-2.5 w-2.5 md:h-3 md:w-3" />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  useHideBottomNav();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const userPlan = user?.metadata?.plan || 'free';
  const isPremium = userPlan !== 'free';

  // Get all images including cover image for lightbox
  const allImages = project ? [
    { url: project.data.coverImage, title: project.data.title },
    ...(project.data.images || [])
  ] : [];

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goToPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  }, [allImages.length]);

  const goToNext = useCallback(() => {
    setLightboxIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  }, [allImages.length]);

  const { likes, isLiked, isBookmarked, toggleLike, toggleBookmark } = useProjectInteractions(
    project?.id,
    project?.data?.category
  );

  // Track and get views - use actual project ID, not URL param (which could be slug)
  const { views } = useProjectViews(project?.id);

  // Print/PDF function (premium only)
  const handlePrint = () => {
    if (!isPremium) {
      alert('Print/PDF export is a premium feature. Please upgrade your plan.');
      return;
    }
    window.print();
  };

  // Fetch project from CMS
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/cms/records/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'X-Project-ID': PLANBASE_CONFIG.projectId,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setProject(data.record);
        } else {
          setError('Project not found');
        }
      } catch (err) {
        console.error('[ProjectDetail] Error:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="relative w-full h-[40vh] md:h-[55vh] bg-muted animate-pulse rounded-2xl">
          <div className="absolute top-4 left-4 h-9 w-24 bg-muted-foreground/20 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="md:col-span-2 space-y-4">
            <div className="h-8 bg-muted rounded animate-pulse" />
            <div className="h-4 bg-muted rounded animate-pulse w-2/3" />
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse" />
              <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-6 space-y-3">
              <div className="h-5 w-32 bg-muted rounded animate-pulse" />
              {[1, 2].map((i) => (
                <div key={i} className="flex justify-between">
                  <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                  <div className="h-4 w-12 bg-muted rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="space-y-4">
        <div className="w-full h-[40vh] md:h-[55vh] bg-muted rounded-2xl flex flex-col items-center justify-center gap-4">
          <Icon icon="hugeicons:image-not-found-01" className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{error || 'Project not found'}</p>
          <Button onClick={() => navigate('/discover')} variant="outline" size="sm">
            <Icon icon="hugeicons:arrow-left-01" className="mr-1.5 h-3.5 w-3.5" />
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }



  return (
    <div className="space-y-4">
      {/* Cover Image */}
      <div 
        className="relative w-full h-[40vh] md:h-[55vh] overflow-hidden bg-muted cursor-pointer rounded-2xl"
        onClick={() => openLightbox(0)}
      >
        {/* Back Button - Overlay on Image */}
        <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/30 bg-black/30 backdrop-blur-sm text-white text-sm active:scale-95 transition-transform"
          >
            <Icon icon="hugeicons:arrow-left-01" className="h-4 w-4" />
            Back
          </button>
        </div>
        {/* Cover Image Only */}
        <img
          src={project.data.coverImage}
          alt={project.data.title}
          className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500"
          onError={(e) => {
            e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
          }}
        />
        {/* Click to expand hint */}
        <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 px-2 py-1 md:px-3 md:py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs md:text-sm">
          Tap to expand
        </div>
      </div>

      {/* Project Info */}
      <div className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Main Content */}
        <div className="md:col-span-2 space-y-4 md:space-y-6">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                  <h1 className="text-lg md:text-xl font-bold text-foreground mb-2 break-words">{project.data.title}</h1>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm md:text-base text-muted-foreground">
                  {project.data.architect && (
                    <span className="flex items-center gap-1">
                      <Icon icon="hugeicons:user-01" className="h-3 w-3 md:h-4 md:w-4 shrink-0" />
                      <span className="truncate">{project.data.architect}</span>
                    </span>
                  )}
                  {project.data.location && (
                    <span className="flex items-center gap-1">
                      <Icon icon="hugeicons:location-01" className="h-3 w-3 md:h-4 md:w-4 shrink-0" />
                      <span className="truncate">{project.data.location}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleLike}
                  className={`h-9 w-9 md:h-10 md:w-10 ${isLiked ? 'text-red-500 border-red-500' : ''}`}
                >
                  <Heart className={`h-4 w-4 md:h-5 md:w-5 ${isLiked ? 'fill-current' : ''}`} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => toggleBookmark(project.data.category)}
                  className={`h-9 w-9 md:h-10 md:w-10 ${isBookmarked ? 'text-orange-500 border-orange-500' : ''}`}
                >
                  <Icon icon="hugeicons:bookmark-01" className={`h-4 w-4 md:h-5 md:w-5 ${isBookmarked ? 'fill-current' : ''}`} />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9 md:h-10 md:w-10">
                  <Icon icon="hugeicons:share-01" className="h-4 w-4 md:h-5 md:w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={handlePrint}
                  title={isPremium ? 'Print / Export PDF' : 'Premium feature'}
                  className={`h-9 w-9 md:h-10 md:w-10 ${!isPremium ? 'opacity-50' : ''}`}
                >
                  {isPremium ? <Icon icon="hugeicons:printer" className="h-4 w-4 md:h-5 md:w-5" /> : <Icon icon="hugeicons:lock" className="h-4 w-4 md:h-5 md:w-5" />}
                </Button>
              </div>
            </div>

            <div className="text-sm md:text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {project.data.description}
            </div>
          </div>

          {/* Additional Images Gallery - Consistent Height Grid */}
          {project.data.images && project.data.images.length > 0 && (
            <div className="space-y-4 md:space-y-6">
              <h3 className="text-lg md:text-xl font-semibold text-foreground">Project Gallery</h3>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
                {project.data.images.map((image: any, index: number) => (
                  <div 
                    key={index} 
                    className="space-y-1.5 md:space-y-2 cursor-pointer group"
                    onClick={() => openLightbox(index + 1)}
                  >
                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                      <img
                        src={typeof image === 'string' ? image : image.url}
                        alt={typeof image === 'object' && image.title ? image.title : `Gallery image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
                        }}
                      />
                    </div>
                    {typeof image === 'object' && (image.title || image.description) && (
                      <div className="space-y-0.5 md:space-y-1">
                        {image.title && (
                          <h4 className="text-xs md:text-sm font-medium text-foreground group-hover:text-orange-500 transition-colors line-clamp-1">{image.title}</h4>
                        )}
                        {image.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 md:line-clamp-2 hidden sm:block">{image.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {project.data.tags && project.data.tags.length > 0 && (
            <div className="space-y-2 md:space-y-3">
              <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
                <Icon icon="hugeicons:tag-01" className="h-3.5 w-3.5 md:h-4 md:w-4" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                {project.data.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm bg-secondary text-secondary-foreground hover:bg-muted rounded-full transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          <div className="bg-card border border-border rounded-2xl overflow-hidden">

            {/* Likes */}
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="flex items-center gap-3 text-sm text-foreground">
                <Heart className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                Likes
              </span>
              <span className="text-sm font-medium text-foreground">{likes}</span>
            </div>

            {/* Views */}
            <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
              <span className="flex items-center gap-3 text-sm text-foreground">
                <Icon icon="hugeicons:view" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                Views
              </span>
              <span className="text-sm font-medium text-foreground">{views}</span>
            </div>

            {/* Category */}
            {project.data.category && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <Icon icon="hugeicons:building-04" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Category
                </span>
                <span className="text-sm text-muted-foreground capitalize">{project.data.category}</span>
              </div>
            )}

            {/* Style */}
            {project.data.style && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <Icon icon="hugeicons:paint-board" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Style
                </span>
                <span className="text-sm text-muted-foreground capitalize">{project.data.style}</span>
              </div>
            )}

            {/* Year */}
            {project.data.year && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <Icon icon="hugeicons:calendar-01" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Year
                </span>
                <span className="text-sm text-muted-foreground">{project.data.year}</span>
              </div>
            )}

            {/* Location */}
            {project.data.location && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <Icon icon="hugeicons:location-01" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Location
                </span>
                <span className="text-sm text-muted-foreground text-right max-w-[55%] truncate">{project.data.location}</span>
              </div>
            )}

            {/* Architect */}
            {project.data.architect && (
              <div className="border-t border-border flex items-center gap-3 px-4 py-3.5">
                <UserAvatar name={project.data.architect} size="sm" className="flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Architect</p>
                  <p className="text-sm text-foreground truncate">{project.data.architect}</p>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={allImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={goToPrev}
          onNext={goToNext}
          tags={project.data.tags}
        />
      )}
    </div>
  );
}
